import React, { ReactElement } from 'react';
import { ViewStyle } from 'react-native';
import { ASSET_TYPE } from '@24i/nxg-sdk-photon';
import { Icon, View, Text } from '@24i/nxg-sdk-quarks';
import { isPlatformWeb } from 'renative';
import AssetLabel from '@24i/nxg-sdk-smartott/src/components/AssetLabel';
import { ResizeModeWeb } from '@24i/nxg-sdk-quarks/src/components/Image/types';
import { useTranslation } from 'react-i18next';
import FastImage from 'react-native-fast-image';
import { ViewModelReturnType } from '../types';
import { selectedStyles } from '../styles';

import {
    defaultRenderChannelOverlay,
    defaultRenderImage,
    defaultRenderLiveBadge,
    defaultRenderPackshotActionButton,
    defaultRenderPackshotTitle,
    defaultRenderProgressBar,
} from '../..';
import { RenderPropsOptions } from '../../../types';
import SeeAllSVG from '../../PackshotTitle/seeAllSVG';

const PackshotContent = (props: ViewModelReturnType): ReactElement => {
    const {
        channelLogo = null,
        contentStyles,
        isGrid = false,
        image = '',
        imageStyles = { height: 0, width: 0 },
        isChannel = false,
        isGenre = false,
        isHighlighted = false,
        isLive = false,
        onLayout,
        onPress,
        packshotGradient = null,
        packshotGradientColors = ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0.7)'],
        progress = 0,
        renderChannelOverlay = defaultRenderChannelOverlay,
        renderImage = defaultRenderImage,
        renderLiveBadge = defaultRenderLiveBadge,
        renderPackshotTitle = defaultRenderPackshotTitle,
        renderPackshotActionButton = defaultRenderPackshotActionButton,
        renderProgressBar = defaultRenderProgressBar,
        resizeMode = 'cover',
        actionIcon = null,
        styles,
        subtitle = '',
        textPlacement = '',
        time = '',
        title,
        index = 0,
        sliderIndex = 0,
        assetLabel,
        fallbackImage,
        type,
        zoomPackshotOnHover = true,
        poster,
        testId = 'packshot',
        isObscured,
        obscuredImage,
        orientation = 'landscape',
        actionButtonStyles,
        liveBadgeStyles,
        progressBarStyles,
        theme,
        channelOverlayStyles,
        packshotTitleStyles,
        dynamicProgress,
        isSelected = false,
        showMetadata = true,
        isGeoBlocked,
        isSeeAll,
        packshotWidth = 0,
        packshotHeight = 0,
    } = props;

    const { colors } = theme.color.gradient2;
    const { t } = useTranslation('sott');

    const renderPropsOptions: RenderPropsOptions = {
        index,
        scopeIndex: sliderIndex,
        colors,
    };

    const isLiveEvent = type === ASSET_TYPE.LIVE_EVENT;
    const isEpisode = type === ASSET_TYPE.EPISODE;
    const isCustom = type === ASSET_TYPE.CUSTOM;
    const showPackshotTitle =
        (isCustom && textPlacement === 'over') || (showMetadata && (!!title || isObscured));

    const hoverLandscapeStyles = ((): ViewStyle => {
        if ('packshotHoverLandscape' in styles && !isHighlighted) {
            return styles.packshotHoverLandscape;
        }
        if ('packshotHoverLandscapeHighlighted' in styles && isHighlighted) {
            return styles.packshotHoverLandscapeHighlighted;
        }
        return {};
    })();

    const hoverPortraitStyles = ((): ViewStyle => {
        if ('packshotHoverPortrait' in styles && !isHighlighted) {
            return styles.packshotHoverPortrait;
        }
        if ('packshotHoverPortraitHighlighted' in styles && isHighlighted) {
            return styles.packshotHoverPortraitHighlighted;
        }
        return {};
    })();

    const renderSeeAll = () => (
        <View style={styles.labelContainer}>
            <View>
                <Text style={styles.label} numberOfLines={1} allowFontScaling={false}>
                    {title}
                </Text>
            </View>
            <View style={styles.arrows}>
                <SeeAllSVG />
            </View>
        </View>
    );

    const renderMetadata = () =>
        renderPackshotTitle(
            {
                styles: packshotTitleStyles,
                isGenre,
                isGrid,
                isChannel,
                textPlacement,
                subtitle: (isObscured ? t('asset.blocked.metadata') : subtitle) || '',
                time,
                title: isObscured ? t('asset.blocked.title') : title,
                infoTestId: isEpisode ? `${type}_${index + 1}` : testId,
                isEpisode,
                isCustom,
                isHighlighted,
                orientation,
                imageStyles: { width: packshotWidth, height: packshotHeight },
            },
            renderPropsOptions
        );

    return (
        <>
            <View
                style={[
                    contentStyles,
                    isSelected ? selectedStyles : null,
                    {
                        ...(isPlatformWeb &&
                            zoomPackshotOnHover &&
                            (poster ? hoverPortraitStyles : hoverLandscapeStyles)),
                    },
                ]}
                onLayout={onLayout}
                renderToHardwareTextureAndroid
                shouldRasterizeIOS
            >
                <View
                    colors={colors || packshotGradientColors}
                    style={[imageStyles as ViewStyle, { backgroundColor: theme.color.darker1 }]}
                >
                    {isSeeAll && renderSeeAll()}
                    {!isGenre &&
                        !isSeeAll &&
                        renderImage(
                            {
                                // LinearGradient is not compatible with type Element
                                // @ts-expect-error
                                children:
                                    isObscured && !isChannel ? (
                                        <View
                                            style={
                                                'obscuredImageContainer' in styles &&
                                                styles.obscuredImageContainer
                                            }
                                        >
                                            <Icon
                                                font="materialIcons"
                                                name="lock"
                                                color={theme.color.lighter2}
                                                style={'iconStyle' in styles && styles.iconStyle}
                                            />
                                        </View>
                                    ) : (
                                        packshotGradient
                                    ),
                                style: imageStyles,
                                source: isObscured ? obscuredImage : { uri: image },
                                draggable: false,
                                noFallback: isObscured,
                                resizeMode: resizeMode as FastImage.ResizeMode & ResizeModeWeb,
                                fallbackImage,
                                hasGradientAlready: !!colors || !!packshotGradientColors,
                                theme,
                            },
                            renderPropsOptions
                        )}
                </View>
                <View style={styles.topContentContainer}>
                    {!!assetLabel && (
                        <View style={styles.assetLabelContainer}>
                            <AssetLabel text={assetLabel} />
                        </View>
                    )}
                    {isGeoBlocked && (
                        <View style={styles.geoblockIconContainer}>
                            <Icon
                                font={theme?.icons?.details?.geoBlocked?.iconFont}
                                name={theme?.icons?.details?.geoBlocked?.iconName}
                                size={isPlatformWeb ? 16 : 14}
                                color={theme.color.contrast1}
                            />
                        </View>
                    )}
                </View>
                {!isLiveEvent &&
                    !!channelLogo &&
                    renderChannelOverlay(
                        {
                            hasBigChannelLogo: isChannel,
                            channelLogo,
                            orientation,
                            styles: channelOverlayStyles,
                            theme,
                            progressBarDisplayed: progress > 0,
                        },
                        renderPropsOptions
                    )}
                {isLive &&
                    renderLiveBadge(
                        {
                            styles: liveBadgeStyles,
                            hasProgressBar: !isLiveEvent && progress > 0,
                        },
                        renderPropsOptions
                    )}
                {!isLiveEvent &&
                    progress > 0 &&
                    renderProgressBar(
                        {
                            progress: isLive || isChannel ? dynamicProgress : progress,
                            theme,
                            styles: progressBarStyles,
                            isLive,
                        },
                        renderPropsOptions
                    )}
                {!!actionIcon &&
                    !isObscured &&
                    !isChannel &&
                    !isSeeAll &&
                    renderPackshotActionButton(
                        {
                            onPress,
                            styles: actionButtonStyles,
                            isHighlighted,
                            actionIcon,
                            overrideDisplayIcon: !isCustom,
                        },
                        renderPropsOptions
                    )}
                {isObscured && !!channelLogo && isChannel && (
                    <View
                        style={
                            'obscuredChannelContainer' in styles && styles.obscuredChannelContainer
                        }
                    >
                        <Icon
                            font="materialIcons"
                            name="lock"
                            size={isPlatformWeb ? 32 : 24}
                            color={theme.color.lighter2}
                        />
                    </View>
                )}
                {showPackshotTitle && isCustom && textPlacement === 'over' && renderMetadata()}
            </View>
            {showPackshotTitle && !isSeeAll && textPlacement !== 'over' && renderMetadata()}
        </>
    );
};

export default PackshotContent;
